import styles from "./index.module.css";

import React from 'react';
import DialogComponent, {DIALOG_TYPE} from 'Core/components/DialogComponent';
import PropTypes from 'prop-types';
import * as pageConfig from "../../config";
import Label from 'Core/components/display/Label';
import {showErrorMessage, showSuccessMessage} from 'Core/helpers/message';
import Button, {BUTTON_STYLE} from 'Core/components/display/Button';

class ExternalQuestionnaireUrlDialog extends DialogComponent {
	constructor(props) {
		super(props, {
			dialogType: DIALOG_TYPE.MESSAGE,
			translationPath: `${pageConfig.translationPath}.ExternalQuestionnaireUrlDialog`,
			optimizedUpdate: true,
		});
		
		this.state = {
			/**
			 * A flag indicating whether the initial attempt to copy the URL to the clipboard has failed.
			 * @type {boolean}
			 */
			initialClipboardFailed: false,
		};
		
		// Action methods
		this.copyUrl = this.copyUrl.bind(this);
	}
	
	/** @inheritdoc */
	async asyncComponentDidMount() {
		await super.asyncComponentDidMount();
		
		this.copyUrl(false)
			.then(success => this.setState({initialClipboardFailed: !success}));
	}


	// Action methods ---------------------------------------------------------------------------------------------------
	/**
	 * Copies the external questionnaire URL to the clipboard.
	 * 
	 * @param {boolean} [showMessage=true] - Whether to show a message after copying the URL.
	 * @return {Promise<boolean>}
	 */
	copyUrl(showMessage = true) {
		const {url} = this.props;
		
		if (navigator.clipboard) {
			return navigator.clipboard.writeText(url)
				.then(() => {
					if (showMessage) showSuccessMessage(this.t('urlCopied'));
					return true;
				})
				.catch(() => {
					if (showMessage) showErrorMessage(this.t('urlCopyFailed'));
					return false;
				});
		} else {
			try {
				document.execCommand("copy");
				if (showMessage) showSuccessMessage(this.t('urlCopied'));
				return Promise.resolve(true);
			} catch (e) {
				if (showMessage) showErrorMessage(this.t('urlCopyFailed'));
				return Promise.resolve(false);
			}
		}
	}
	


	// Render methods ---------------------------------------------------------------------------------------------------
	render() {
		const {url} = this.props;
		const {initialClipboardFailed} = this.state;
		
		return this.renderDialog(
			this.renderCustomIconTitle('external-link', this.t('title')),
			(
				<div className={styles['wrapper']}>
					{initialClipboardFailed ?
						<div className="text-center warning-color">
							<Label content={this.t('warningMessage')} supportHtml={true} />
						</div>
						:
						<div className="text-center success-color">
							<Label content={this.t('successMessage')} supportHtml={true} />
						</div>
					}
					<div className="text-center">
						<Label content={this.t('onePatientNotice')} supportHtml={true} />
					</div>
					<br />
					<div className={`notice ${styles['urlNotice']}`}>
						<Button
							icon="copy"
							title={this.t('clickToCopy')}
							displayStyle={BUTTON_STYLE.ACTION}
							onClick={() => this.copyUrl()}
						/>
						<Label element="code" content={url} />
					</div>
				</div>
			)
		);
	}
}

/**
 * Define the component's own props that can be passed to it by parent components
 */
ExternalQuestionnaireUrlDialog.propTypes = {
	url: PropTypes.string,
};

export default ExternalQuestionnaireUrlDialog;